// @flow
// $FlowFixMe - Cannot import useState because there is no useState export in react.
import React, { useState } from 'react';
import {
  SelectCreatableHookForm,
  ConfirmationModal,
  toastError,
  toastSuccess,
} from 'bench-internal-theme';
import { useFormContext } from 'react-hook-form';
import { useQuery, useMutation } from '@apollo/client';
import { showServerError } from 'bk-web-app';
import { openModal } from 'modal-core-components';
import { useClient } from '../../ClientContext';
import { GET_FINANCIAL_INSTITUTIONS, CREATE_FINANCIAL_INSTITUTION } from '../queries';
import { institutionOptions } from './formOptions';
import type { LedgerAccount } from '../types';
import type { SelectOption } from 'bench-internal-theme';

type InstitutionSelectContainerProps = {
  isSubmitting: boolean,
  ledgerAccount: ?LedgerAccount,
  isTemporary: boolean,
};

const InstitutionSelect = ({
  isSubmitting,
  ledgerAccount,
  isTemporary,
}: InstitutionSelectContainerProps) => {
  const { setValue } = useFormContext();
  const [newFinancialInstitutionId, setNewFinancialInstitutionId] = useState(undefined);

  const { loading, error, data } = useQuery(GET_FINANCIAL_INSTITUTIONS, {
    fetchPolicy: 'cache-and-network',
  });
  const [createFinancialInstitution, { loading: createInstitutionLoading }] = useMutation(
    CREATE_FINANCIAL_INSTITUTION,
    {
      awaitRefetchQueries: true,
      onCompleted: response => {
        setNewFinancialInstitutionId(response?.createFinancialInstitution?.id);
        toastSuccess('Institution Created', 'Your institution has been created.');
      },
      onError: error => {
        toastError(
          'An error occurred creating this institution.',
          showServerError(error) || error.message,
        );
      },
      refetchQueries: [{ query: GET_FINANCIAL_INSTITUTIONS }],
    },
  );

  const institutions = data?.financialInstitutions || [];
  const options = institutionOptions(institutions);

  const client = useClient();

  const onConfirmCreate = institutionName => {
    setValue('institution', null);

    const payload = {
      variables: {
        clientId: client.id,
        institution: {
          name: institutionName,
          isActive: true,
        },
        populateDefaults: true,
      },
    };

    return createFinancialInstitution(payload).then(() => {
      setValue(
        'institution',
        options.find(option => option.value === newFinancialInstitutionId),
      );
    });
  };

  const createFinancialInstitutionConfirmation = institutionName => {
    openModal(closeModal => (
      <ConfirmationModal
        closeModal={closeModal}
        onConfirm={() => onConfirmCreate(institutionName)}
        title="Are you sure?"
        description="New institutions should only be created if you have a PDF or bank connection that states the institution`s full name."
      />
    ));
  };

  const addInactiveInstitutionConfirmation = (institution: SelectOption) => {
    openModal(closeModal => (
      <ConfirmationModal
        closeModal={closeModal}
        onConfirm={() => setValue('institution', institution)}
        title="Are you sure?"
        description="This institution is inactive. Would you still like to continue?"
      />
    ));
  };

  const onChangeInstitution = (institution: SelectOption) => {
    if (institution?.value && !institution?.metaData?.isActive) {
      return addInactiveInstitutionConfirmation(institution);
    }

    setValue('institution', institution);
  };

  const getPlaceholderText = () => {
    if (error) {
      return 'A problem occurred fetching institutions';
    }
    return 'Search...';
  };

  return (
    <SelectCreatableHookForm
      aria-label="Institution"
      defaultValue={
        newFinancialInstitutionId ||
        ledgerAccount?.financialInstitutionProduct?.financialInstitution?.id
      }
      isClearable
      onChange={onChangeInstitution}
      isDisabled={isSubmitting || !!error}
      isLoading={loading || createInstitutionLoading}
      name="institution"
      onCreateOption={createFinancialInstitutionConfirmation}
      options={options}
      placeholder={getPlaceholderText()}
      rules={{ required: { value: !isTemporary, message: 'Institution is required' } }}
    />
  );
};

InstitutionSelect.defaultProps = {
  ledgerAccount: undefined,
};

export default InstitutionSelect;
